import $ from "jquery";
import * as showdown from 'showdown';
import AjaxUtils from "../../util/ajax_utils";
import Vue from 'vue';
import { convertPluginToSchema } from "./scan_utils";
import { ConfigForm } from "../config_form";


/**
 * scan_plugins_v2.js
 *
 * New plugin manager field component with:
 *  - Single request to load all plugins (client-side grouping by category)
 *  - Search bar with filtering across name, description, category
 *  - Tri-state category checkboxes (all/none/indeterminate)
 *  - Bigger rows with visible description teasers
 *  - Backward compatible hidden field output
 *
 * Supports two integration modes:
 *  1) Vue config form mode  — receives `fields` prop with mapping-based access
 *  2) Mako mount mode       — receives `name`, `initialDisabled`, `initialOptions` props
 *                              and writes to a hidden <input>
 */


var PluginWindow = {
    components: {
        'config-form': ConfigForm,
    },
    props: {
        plugin: {
            type: Object,
            default: {},
        },
        value: {
            type: Object,
            default: {},
        },
    },
    data: function () {
        return {
            schema: null
        };
    },
    methods: {
        updateValue(value) {
            this.$emit('input', value);
        },
    },
    mounted: function () {
        this.schema = convertPluginToSchema(this.plugin);
    },
    template: `
        <config-form
            v-if="schema && schema.fields"
            :schema="schema"
            :value="value"
            :collapsed="false"
            :collapsible="false"
            :header="false"
            v-on:input="updateValue"
        >
        </config-form>
    `
};


// ---------------------------------------------------------------------------
// PluginSearchBar
// ---------------------------------------------------------------------------
var PluginSearchBar = {
    props: {
        value: { type: String, default: '' },
        totalCount: { type: Number, default: 0 },
        visibleCount: { type: Number, default: 0 }
    },
    data: function () {
        return { timer: null };
    },
    methods: {
        onInput: function (e) {
            var cmp = this;
            clearTimeout(cmp.timer);
            cmp.timer = setTimeout(function () {
                cmp.$emit('input', e.target.value);
            }, 250);
        },
        clear: function () {
            this.$emit('input', '');
        }
    },
    template: '\
        <div class="plugin-search-bar">\
            <div class="plugin-search-bar__input-wrap">\
                <i class="fa fa-search plugin-search-bar__icon"></i>\
                <input\
                    type="text"\
                    class="plugin-search-bar__input"\
                    placeholder="Search plugins by name, description or category\u2026"\
                    :value="value"\
                    v-on:input="onInput"\
                />\
                <a v-if="value" href="javascript:;" class="plugin-search-bar__clear" v-on:click="clear">\
                    <i class="fa fa-times"></i>\
                </a>\
            </div>\
            <span class="plugin-search-bar__count" v-if="value">\
                Showing {{ visibleCount }} of {{ totalCount }} plugins\
            </span>\
        </div>\
    '
};


// ---------------------------------------------------------------------------
// PluginItemRow
// ---------------------------------------------------------------------------
var PluginItemRow = {
    props: {
        plugin: { type: Object, required: true },
        enabled: { type: Boolean, default: true },
        pluginOptions: { type: Object, default: function () { return {}; } }
    },
    data: function () {
        return {
            expandedDescription: false,
            description: '',
            teaser: '',
            group: []
        };
    },
    computed: {
        rowDescription: function () {
            return this.expandedDescription ? this.description : this.teaser;
        },
        hasOptions: function () {
            return this.plugin.options && this.plugin.options.length > 0;
        },
        pluginValue: function() {
            if (this.plugin._id in this.pluginOptions)
                return this.pluginOptions[this.plugin._id];
            return {};
        }
    },
    methods: {
        togglePlugin: function () {
            this.$emit('toggle-plugin', this.plugin._id);
        },
        toggleDescription: function () {
            this.expandedDescription = !this.expandedDescription;
        },
        openConfiguration: function () {
            var cmp = this;
            this.$lightbox.open({
                title: 'Configure plugin - ' + cmp.plugin.name,
                component: PluginWindow,
                bind: {
                    plugin: cmp.plugin,
                    value: cmp.pluginValue,
                },
                on: {
                    input: cmp.updateValue,
                },
                size: 'normal'
            });
        },
        updateValue(value) {
            this.$emit('options-changed', { pluginId: this.plugin._id, value });
        },
    },
    mounted: function () {
        var converter = new showdown.Converter();
        var raw = (this.plugin.description || '').trim().replace(/ {4,}/g, ' ');
        this.description = converter.makeHtml(raw);
        this.teaser = $(this.description).text().replace('Synopsis', '').trim();
        // Truncate teaser to ~200 chars
        if (this.teaser.length > 200) {
            this.teaser = this.teaser.substring(0, 200) + '\u2026';
        }
    },
    template: '\
        <tr class="plugin-item-row">\
            <td class="plugin-item-row__check">\
                <input\
                    type="checkbox"\
                    :checked="enabled"\
                    v-on:change="togglePlugin"\
                />\
            </td>\
            <td class="plugin-item-row__name">\
                <span v-if="hasOptions">\
                    <a href="javascript:;" v-on:click="openConfiguration">{{ plugin.name }}</a>\
                </span>\
                <span v-else>{{ plugin.name }}</span>\
            </td>\
            <td class="plugin-item-row__desc" v-on:click="toggleDescription">\
                <div :class="[\'plugin-item-row__desc-text\', expandedDescription ? \'plugin-item-row__desc-text--full\' : \'\']">\
                    <div v-if="expandedDescription" v-html="description"></div>\
                    <span v-else>{{ teaser }}</span>\
                </div>\
            </td>\
            <td class="plugin-item-row__actions">\
                <a v-if="hasOptions" href="javascript:;" v-on:click="openConfiguration" title="Configure plugin">\
                    <i class="fa fa-cog"></i>\
                </a>\
                <a href="javascript:;" v-on:click="toggleDescription" :title="expandedDescription ? \'Collapse\' : \'Expand\'">\
                    <i :class="[\'fa\', expandedDescription ? \'fa-angle-up\' : \'fa-angle-down\']"></i>\
                </a>\
            </td>\
        </tr>\
    '
};


// ---------------------------------------------------------------------------
// PluginCategoryRow
// ---------------------------------------------------------------------------
var PluginCategoryRow = {
    components: {
        'plugin-item-row': PluginItemRow
    },
    props: {
        category: { type: String, required: true },
        plugins: { type: Array, required: true },
        disabled: { type: Array, required: true },
        pluginOptions: { type: Object, required: true },
        initialExpanded: { type: Boolean, default: false }
    },
    data: function () {
        return {
            expanded: this.initialExpanded
        };
    },
    computed: {
        enabledCount: function () {
            var cmp = this;
            var count = 0;
            cmp.plugins.forEach(function (p) {
                if (cmp.disabled.indexOf(p._id) === -1) {
                    count++;
                }
            });
            return count;
        },
        checkState: function () {
            if (this.enabledCount === this.plugins.length) return 'all';
            if (this.enabledCount === 0) return 'none';
            return 'indeterminate';
        },
        isChecked: function () {
            return this.checkState === 'all';
        }
    },
    watch: {
        initialExpanded: function (val) {
            this.expanded = !!val;
        },
        checkState: function () {
            this.updateIndeterminate();
        }
    },
    methods: {
        updateIndeterminate: function () {
            var cb = this.$refs.categoryCb;
            if (cb) {
                cb.indeterminate = (this.checkState === 'indeterminate');
            }
        },
        toggleExpand: function () {
            this.expanded = !this.expanded;
            this.$emit('expand-changed', {
                category: this.category,
                expanded: this.expanded
            });
        },
        toggleCategory: function () {
            var ids = this.plugins.map(function (p) { return p._id; });
            if (this.checkState === 'none') {
                // Enable all
                this.$emit('enable-plugins', ids);
            } else {
                // Disable all (from checked or indeterminate)
                this.$emit('disable-plugins', ids);
            }
        },
        onTogglePlugin: function (pluginId) {
            this.$emit('toggle-plugin', pluginId);
        },
        onOptionsChanged: function (payload) {
            this.$emit('options-changed', payload);
        },
        isPluginEnabled: function (plugin) {
            return this.disabled.indexOf(plugin._id) === -1;
        }
    },
    mounted: function () {
        this.updateIndeterminate();
    },
    updated: function () {
        this.updateIndeterminate();
    },
    template: '\
        <li class="plugin-category">\
            <div class="plugin-category__header" v-on:click="toggleExpand">\
                <span class="plugin-category__expander">\
                    <i :class="[\'fa\', expanded ? \'fa-caret-down\' : \'fa-caret-right\']"></i>\
                </span>\
                <span class="plugin-category__check" v-on:click.stop>\
                    <input\
                        ref="categoryCb"\
                        type="checkbox"\
                        :checked="isChecked"\
                        v-on:change="toggleCategory"\
                    />\
                </span>\
                <span class="plugin-category__title">{{ category }}</span>\
                <span class="plugin-category__count">({{ enabledCount }}/{{ plugins.length }})</span>\
            </div>\
            <div v-if="expanded" class="plugin-category__body">\
                <table class="apck-table plugin-table">\
                    <colgroup>\
                        <col style="width: 40px;">\
                        <col style="width: 200px;">\
                        <col style="width: auto;">\
                        <col style="width: 70px;">\
                    </colgroup>\
                    <thead>\
                        <tr>\
                            <th></th>\
                            <th>Name</th>\
                            <th>Description</th>\
                            <th></th>\
                        </tr>\
                    </thead>\
                    <tbody>\
                        <plugin-item-row\
                            v-for="p in plugins"\
                            :key="p._id"\
                            :plugin="p"\
                            :enabled="isPluginEnabled(p)"\
                            :plugin-options="pluginOptions"\
                            v-on:toggle-plugin="onTogglePlugin"\
                            v-on:options-changed="onOptionsChanged"\
                        />\
                    </tbody>\
                </table>\
            </div>\
        </li>\
    '
};


// ---------------------------------------------------------------------------
// ApckPluginManagerFieldV2  (root)
// ---------------------------------------------------------------------------
export var ApckPluginManagerFieldV2 = {
    components: {
        'plugin-search-bar': PluginSearchBar,
        'plugin-category-row': PluginCategoryRow
    },
    props: {
        // Vue config form mode
        fields: { type: Array, default: function () { return []; } },
        // Endpoint URL
        src: { type: String, default: '/scans/plugins/all' },
        // Mako mount mode
        name: { type: String, default: '' },
        initialDisabled: { type: Array, default: function () { return []; } },
        initialOptions: { type: Object, default: function () { return {}; } },
        initialHasSavedState: { type: Boolean, default: false }
    },
    data: function () {
        return {
            allPlugins: [],
            disabled: [],
            pluginOptions: {},
            searchQuery: '',
            expandedCategories: {},
            searchExpandedCategoriesSnapshot: null,
            hasExplicitPluginState: false,
            loading: true
        };
    },
    computed: {
        // Is this used inside a Vue config form (via fields prop)?
        isFieldsMode: function () {
            return this.fields && this.fields.length > 0;
        },
        // Filter plugins by search query
        filteredPlugins: function () {
            if (!this.searchQuery) return this.allPlugins;
            var q = this.searchQuery.toLowerCase();
            return this.allPlugins.filter(function (p) {
                return (p.name && p.name.toLowerCase().indexOf(q) !== -1) ||
                       (p.description && p.description.toLowerCase().indexOf(q) !== -1) ||
                       (p.category && p.category.toLowerCase().indexOf(q) !== -1);
            });
        },
        // Group filtered plugins by category, sorted alphabetically
        groupedPlugins: function () {
            var groups = {};
            this.filteredPlugins.forEach(function (p) {
                var cat = p.category || 'Uncategorised';
                if (!groups[cat]) groups[cat] = [];
                groups[cat].push(p);
            });
            var sorted = Object.keys(groups).sort();
            return sorted.map(function (cat) {
                return { category: cat, plugins: groups[cat] };
            });
        },
        totalPluginCount: function () {
            return this.allPlugins.length;
        },
        visiblePluginCount: function () {
            return this.filteredPlugins.length;
        },
        // JSON value for hidden input (Mako mode)
        serializedValue: function () {
            return JSON.stringify({
                disabled: this.disabled,
                pluginOptions: this.pluginOptions
            });
        }
    },
    methods: {
        // --- field mapping helpers (Vue config form mode) ---
        getValueByMapping: function (mapping) {
            var idx = this.fields.findIndex(function (f) { return f.mapping === mapping; });
            if (idx === -1) return '';
            return this.fields[idx].value;
        },
        setValueByMapping: function (mapping, value) {
            var idx = this.fields.findIndex(function (f) { return f.mapping === mapping; });
            if (idx !== -1) {
                this.fields[idx].value = value;
            }
        },
        loadPluginsSettings: function () {
            if (this.isFieldsMode) {
                var exclude = this.getValueByMapping('settings.exclude_plugins');
                var opts = this.getValueByMapping('settings.plugins');
                this.hasExplicitPluginState = !!(exclude || (opts && Object.keys(opts).length));
                this.disabled = exclude ? exclude.split(/\r?\n/).filter(function (s) { return s; }) : [];
                this.pluginOptions = opts || {};
            } else {
                this.disabled = (this.initialDisabled || []).slice();
                this.pluginOptions = $.extend(true, {}, this.initialOptions || {});
                this.hasExplicitPluginState = !!this.initialHasSavedState;
            }

            this.applyDefaultDisabledPlugins();
        },
        syncBack: function () {
            if (this.isFieldsMode) {
                this.setValueByMapping('settings.exclude_plugins', this.disabled.join('\n'));
            }
            // In Mako mode the hidden input auto-updates via serializedValue computed
        },

        // --- plugin toggle ---
        togglePlugin: function (pluginId) {
            var idx = this.disabled.indexOf(pluginId);
            if (idx !== -1) {
                this.disabled.splice(idx, 1);
                this.ensurePluginOptions(pluginId);
            } else {
                this.disabled.push(pluginId);
            }
            this.syncBack();
        },
        enablePlugins: function (ids) {
            var cmp = this;
            ids.forEach(function (id) {
                var idx = cmp.disabled.indexOf(id);
                if (idx !== -1) {
                    cmp.disabled.splice(idx, 1);
                }
                cmp.ensurePluginOptions(id);
            });
            cmp.syncBack();
        },
        disablePlugins: function (ids) {
            var cmp = this;
            ids.forEach(function (id) {
                if (cmp.disabled.indexOf(id) === -1) {
                    cmp.disabled.push(id);
                }
            });
            cmp.syncBack();
        },
        ensurePluginOptions: function (pluginId) {
            if (this.pluginOptions[pluginId]) return;
            var plugin = this.allPlugins.find(function (p) { return p._id === pluginId; });
            if (plugin && plugin.options) {
                var opts = {};
                plugin.options.forEach(function (o) {
                    opts[o.attr_name] = o['default'];
                });
                Vue.set(this.pluginOptions, pluginId, opts);
            }
        },
        onOptionsChanged: function (payload) {
            Vue.set(this.pluginOptions, payload.pluginId, payload.value);
            this.syncBack();
        },
        applyDefaultDisabledPlugins: function () {
            if (this.hasExplicitPluginState || !this.allPlugins.length || this.disabled.length) {
                return;
            }

            this.disabled = this.allPlugins
                .filter(function (plugin) { return plugin.enabled === false; })
                .map(function (plugin) { return plugin._id; });

            this.syncBack();
        },
        onCategoryExpandChanged: function (payload) {
            Vue.set(this.expandedCategories, payload.category, payload.expanded);
        },
        cloneExpandedCategories: function (source) {
            return $.extend(true, {}, source || {});
        },
        isCategoryExpanded: function (cat) {
            return !!this.expandedCategories[cat];
        }
    },
    watch: {
        fields: {
            handler: function () {
                this.loadPluginsSettings();
            },
            deep: true
        },
        // When searching, auto-expand matching categories
        searchQuery: function (val, oldVal) {
            if (val) {
                if (!oldVal) {
                    this.searchExpandedCategoriesSnapshot = this.cloneExpandedCategories(this.expandedCategories);
                }
                var cmp = this;
                cmp.groupedPlugins.forEach(function (g) {
                    Vue.set(cmp.expandedCategories, g.category, true);
                });
            } else if (oldVal) {
                this.expandedCategories = this.cloneExpandedCategories(this.searchExpandedCategoriesSnapshot);
                this.searchExpandedCategoriesSnapshot = null;
            }
        }
    },
    mounted: function () {
        var cmp = this;
        cmp.loadPluginsSettings();
        AjaxUtils.ajax_get({
            url: cmp.src,
            success: function (data) {
                if (data && data.items) {
                    cmp.allPlugins = data.items;
                    cmp.applyDefaultDisabledPlugins();
                }
                cmp.loading = false;
            }
        });
    },
    template: '\
        <div class="panel datatable treetable plugin-manager-v2">\
            <div class="plugin-manager-v2__toolbar">\
                <plugin-search-bar\
                    v-model="searchQuery"\
                    :total-count="totalPluginCount"\
                    :visible-count="visiblePluginCount"\
                />\
            </div>\
            <div class="panel-no-padding scrollx">\
                <div v-if="loading" class="plugin-manager-v2__loading">\
                    <i class="fa fa-spinner fa-spin"></i> Loading plugins\u2026\
                </div>\
                <div v-else >\
                    <ul>\
                        <plugin-category-row\
                            v-for="g in groupedPlugins"\
                            :key="g.category"\
                            :category="g.category"\
                            :plugins="g.plugins"\
                            :disabled="disabled"\
                            :plugin-options="pluginOptions"\
                            :initial-expanded="!!expandedCategories[g.category]"\
                            v-on:toggle-plugin="togglePlugin"\
                            v-on:enable-plugins="enablePlugins"\
                            v-on:disable-plugins="disablePlugins"\
                            v-on:expand-changed="onCategoryExpandChanged"\
                            v-on:options-changed="onOptionsChanged"\
                        />\
                    </ul>\
                    <p v-if="groupedPlugins.length === 0 && searchQuery" class="plugin-manager-v2__empty">\
                        No plugins match &ldquo;{{ searchQuery }}&rdquo;\
                    </p>\
                </div>\
            </div>\
            <input v-if="name" type="hidden" :name="name" :value="serializedValue" />\
        </div>\
    '
};
